import { create } from "zustand";

/**
 * 「담기 완료」 팝의 전역 상태 (#5366).
 *
 * ## 왜 전역인가 — 트리거와 앵커가 다른 곳에 있다
 *
 * 담기 버튼은 **페이지 안쪽 제품 카드**에 있고(`NotifyOrAddButton`), 팝이 붙는 자리는
 * **GNB 의 장바구니 아이콘**이다(기획: 아이콘 영역 아래 20px · 우측정렬). 두 컴포넌트가
 * 트리 상 멀어서 props 로 이을 수 없다 — 형제 `useBundledStore`(번들 병합 모달)와 같은
 * 이유·같은 형태다.
 *
 * ## SKU 를 담는다 (slug 가 아니다)
 *
 * 팝은 카트 전체가 아니라 **방금 담은 항목**을 보여주고, 색·사이즈 옵션이 있는 제품은
 * slug 하나에 SKU 가 여럿이다. 카트에서 그 줄을 찾는 키가 SKU 다.
 *
 * ## SKU **목록**이다 — 옵션 다이얼로그는 한 번에 여러 개를 담는다
 *
 * 처음엔 SKU 하나(`sku: string | null`)였고, 그래서 옵션 다이얼로그(`SkuOptionsDialog` — 배지 색 여러 개를
 * 한 번에 담음)는 2개 이상 담기면 팝 대신 `/cart` 로 이동했다(#5480 — 하나만 보여 주면 담은 것보다 적게
 * 말한다). 사용자 지적(2026-10-07 · `/tactsuit/accessories` 배지 모든 타입 담기 → 카트 이동)으로 목록을
 * 받게 했다. 한 개만 담는 호출처(카드·옵션 시트·`useAddItemToCart`)는 SKU 하나를 그대로 넘긴다.
 */
type AddedToBagState = {
  /** 방금 담은 SKU 들(담은 순서). 비어 있으면 닫힘. */
  skus: string[];
};

type AddedToBagAction = {
  /** SKU 하나 또는 여러 개. 빈 값만 남으면 열지 않는다. */
  open: (skus: string | string[]) => void;
  close: () => void;
};

type AddedToBagStore = AddedToBagState & AddedToBagAction;

export const addedToBagStore = create<AddedToBagStore>()((set) => ({
  skus: [],
  open: (skus) =>
    set({ skus: (Array.isArray(skus) ? skus : [skus]).filter(Boolean) }),
  close: () => set({ skus: [] }),
}));

export const useAddedToBagStore = <T>(
  selector: (state: AddedToBagStore) => T,
): T => addedToBagStore(selector);

export const getAddedToBagState = () => addedToBagStore.getState();
